import { t } from '../i18n/i18n';
import { UiState } from '../state/UiState';
import { LicenseService } from '../services/LicenseService';

export class UpgradeModal {
  private element: HTMLElement;
  private uiState?: UiState;
  private licenseService: LicenseService;

  private featureTitleEl: HTMLElement | null = null;
  private headingEl: HTMLElement | null = null;
  private perksEl: HTMLElement | null = null;
  private buyBtnEl: HTMLAnchorElement | null = null;
  private saleBadgeEl: HTMLElement | null = null;
  private closeFooterBtnEl: HTMLButtonElement | null = null;

  // ライセンス入力関連
  private licenseInactiveView: HTMLElement | null = null;
  private licenseActiveView: HTMLElement | null = null;
  private licenseLabelEl: HTMLElement | null = null;
  private inputKeyEl: HTMLInputElement | null = null;
  private btnActivateEl: HTMLButtonElement | null = null;
  private licenseStatusEl: HTMLElement | null = null;
  private activeBadgeTextEl: HTMLElement | null = null;
  private activeKeyDetailsEl: HTMLElement | null = null;
  private btnDeactivateEl: HTMLButtonElement | null = null;

  private isActivating = false;

  constructor(uiState?: UiState) {
    this.uiState = uiState;
    this.licenseService = LicenseService.getInstance();

    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop';
    this.element.id = 'modalUpgrade';
    this.render();

    // ライセンス状態変更時にUIを自動再描画
    this.licenseService.subscribe(() => {
      this.syncLicenseUI();
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  show(featureName?: string): void {
    this.updateTexts();
    this.syncLicenseUI();
    if (this.featureTitleEl && featureName) {
      this.featureTitleEl.textContent = featureName;
    }
    this.element.classList.add('show');
  }

  hide(): void {
    this.element.classList.remove('show');
  }

  private render(): void {
    const texts = t().upgrade;

    this.element.innerHTML = `
      <div class="modal-card" style="max-width: 500px; max-height: 90vh; overflow-y: auto;">
        <div class="modal-header">
          <div class="modal-title">
            <span>👑</span>
            <span id="upgradeFeatureTitle">${texts.title}</span>
          </div>
          <button class="modal-close" id="btnUpgradeClose">&times;</button>
        </div>
        <div class="modal-body" style="padding: 20px 24px;">
          <!-- ヒーロータイトル & 特典一覧 -->
          <div style="text-align: center; margin-bottom: 16px;">
            <div style="font-size: 2.2rem; margin-bottom: 6px;">✨</div>
            <h3 id="upgradeHeading" style="font-size: 1.15rem; font-weight: 700; margin-bottom: 10px;">${texts.heading}</h3>
            <div id="upgradePerks" style="font-size: 13px; color: var(--color-text-main); line-height: 1.7; text-align: left; background: #faf8f5; padding: 12px 16px; border-radius: var(--radius-sm); border: 1px solid var(--color-card-border); margin-bottom: 14px;">
              ・${texts.perkUrl}<br>
              ・${texts.perkPreset}<br>
              ・${texts.perkExport}<br>
              ・${texts.perkAudio}<br>
              ・${texts.perkWobble}
            </div>
          </div>

          <!-- 1. Gumroad 購入ボタン -->
          <div id="upgradePurchaseSection" style="margin-bottom: 18px;">
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 8px;">
              <span id="upgradeSaleBadge" style="background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff; font-size: 11px; font-weight: 800; padding: 3px 10px; border-radius: var(--radius-pill); letter-spacing: 0.5px; box-shadow: 0 2px 6px rgba(239, 68, 68, 0.3);">
                ${texts.saleBadge}
              </span>
              <span style="font-size: 13px; color: var(--color-text-muted); text-decoration: line-through; font-weight: 600;">$2.99</span>
              <span style="font-size: 15px; font-weight: 800; color: #dc2626;">→ $1.99</span>
            </div>
            <a id="btnUpgradeBuyGumroad" class="btn btn-primary" style="display: block; width: 100%; text-align: center; padding: 12px; font-size: 14px; font-weight: 700; text-decoration: none; box-sizing: border-box; background: linear-gradient(135deg, #ff9a44, #fc6076); box-shadow: 0 4px 14px rgba(252, 96, 118, 0.35); cursor: pointer;" target="_blank" rel="noopener noreferrer">
              ${texts.buyBtn}
            </a>
          </div>

          <!-- 区切り線 -->
          <div style="position: relative; text-align: center; margin: 20px 0 16px;">
            <hr style="border: none; border-top: 1px dashed var(--color-card-border);" />
            <span style="position: absolute; top: -9px; left: 50%; transform: translateX(-50%); background: white; padding: 0 12px; font-size: 11px; color: var(--color-text-muted); font-weight: 600;">
              OR
            </span>
          </div>

          <!-- 2. ライセンスキー認証セクション -->
          <div id="upgradeLicenseSection" style="background: #ffffff; border: 1px solid var(--color-card-border); border-radius: var(--radius-md); padding: 16px;">
            <!-- 未認証状態 -->
            <div id="licenseInactiveView">
              <label id="licenseLabel" style="display: block; font-size: 12px; font-weight: 600; margin-bottom: 8px;">
                ${texts.haveKey}
              </label>
              <div style="display: flex; gap: 8px; margin-bottom: 6px;">
                <input
                  type="text"
                  id="inputLicenseKey"
                  placeholder="${texts.keyPlaceholder}"
                  style="flex: 1; padding: 8px 12px; border: 1px solid var(--color-card-border); border-radius: var(--radius-sm); font-size: 12px; font-family: monospace; outline: none; transition: border-color 0.2s;"
                />
                <button type="button" class="btn btn-secondary" id="btnActivateLicense" style="white-space: nowrap; font-size: 12px; font-weight: 600; padding: 8px 14px;">
                  ${texts.activateBtn}
                </button>
              </div>
              <div id="licenseStatusMsg" style="display: none; font-size: 12px; line-height: 1.4; border-radius: var(--radius-sm); padding: 8px 10px; margin-top: 6px;"></div>
            </div>

            <!-- 認証済み状態 -->
            <div id="licenseActiveView" style="display: none; text-align: center; padding: 8px 0;">
              <div style="display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; background: #e6f9ed; color: #15803d; border-radius: var(--radius-pill); font-size: 12px; font-weight: 700; margin-bottom: 8px;">
                <span>✓</span>
                <span id="activeBadgeText">${texts.activeStatus}</span>
              </div>
              <div id="activeKeyDetails" style="font-size: 11px; color: var(--color-text-muted); font-family: monospace; margin-bottom: 12px;"></div>
              <button type="button" class="btn btn-secondary" id="btnDeactivateLicense" style="font-size: 11px; color: #b91c1c; border-color: #fca5a5; padding: 4px 12px;">
                ${texts.deactivateBtn}
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer" style="padding: 12px 24px;">
          <button class="btn btn-secondary" id="btnUpgradeCloseFooter" style="width: 100%;">${texts.closeBtn}</button>
        </div>
      </div>
    `;

    this.featureTitleEl = this.element.querySelector('#upgradeFeatureTitle');
    this.headingEl = this.element.querySelector('#upgradeHeading');
    this.perksEl = this.element.querySelector('#upgradePerks');
    this.buyBtnEl = this.element.querySelector('#btnUpgradeBuyGumroad');
    this.saleBadgeEl = this.element.querySelector('#upgradeSaleBadge');
    this.closeFooterBtnEl = this.element.querySelector('#btnUpgradeCloseFooter');

    this.licenseInactiveView = this.element.querySelector('#licenseInactiveView');
    this.licenseActiveView = this.element.querySelector('#licenseActiveView');
    this.licenseLabelEl = this.element.querySelector('#licenseLabel');
    this.inputKeyEl = this.element.querySelector('#inputLicenseKey');
    this.btnActivateEl = this.element.querySelector('#btnActivateLicense');
    this.licenseStatusEl = this.element.querySelector('#licenseStatusMsg');
    this.activeBadgeTextEl = this.element.querySelector('#activeBadgeText');
    this.activeKeyDetailsEl = this.element.querySelector('#activeKeyDetails');
    this.btnDeactivateEl = this.element.querySelector('#btnDeactivateLicense');

    // イベントバインド
    this.element.querySelector('#btnUpgradeClose')?.addEventListener('click', () => this.hide());
    this.closeFooterBtnEl?.addEventListener('click', () => this.hide());

    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) {
        this.hide();
      }
    });

    this.buyBtnEl?.addEventListener('click', (e) => {
      e.preventDefault();
      window.open(this.licenseService.getBuyUrl(), '_blank');
    });

    this.btnActivateEl?.addEventListener('click', () => {
      this.handleActivate();
    });

    this.inputKeyEl?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleActivate();
      }
    });

    this.btnDeactivateEl?.addEventListener('click', () => {
      this.handleDeactivate();
    });

    this.syncLicenseUI();
  }

  private async handleActivate(): Promise<void> {
    if (this.isActivating || !this.inputKeyEl) return;
    const key = this.inputKeyEl.value.trim();
    const texts = t().upgrade;

    if (!key) {
      this.showStatus(texts.keyPlaceholder, 'error');
      return;
    }

    this.isActivating = true;
    if (this.btnActivateEl) {
      this.btnActivateEl.disabled = true;
      this.btnActivateEl.textContent = texts.activating;
    }
    this.showStatus(texts.activating, 'info');

    try {
      const result = await this.licenseService.verifyAndActivate(key);

      if (result.success) {
        this.showStatus(texts.successMsg, 'success');
        if (this.uiState) {
          this.uiState.setEdition('pc_app_paid');
        }
        setTimeout(() => {
          this.syncLicenseUI();
        }, 800);
      } else {
        let errorMsg = texts.errInvalid;
        if (result.error === 'max_uses_exceeded') {
          errorMsg = texts.errMaxUses;
        } else if (result.error === 'network_error') {
          errorMsg = texts.errNetwork;
        } else if (result.error === 'refunded') {
          errorMsg = texts.errRefunded;
        }
        this.showStatus(errorMsg, 'error');
      }
    } catch {
      this.showStatus(texts.errNetwork, 'error');
    } finally {
      this.isActivating = false;
      if (this.btnActivateEl) {
        this.btnActivateEl.disabled = false;
        this.btnActivateEl.textContent = texts.activateBtn;
      }
    }
  }

  private handleDeactivate(): void {
    const texts = t().upgrade;
    if (confirm(texts.deactivateBtn + ' ?')) {
      this.licenseService.deactivate();
      if (this.uiState) {
        this.uiState.setEdition('web_free');
      }
      this.syncLicenseUI();
      this.showStatus(texts.deactivatedMsg, 'info');
    }
  }

  private showStatus(msg: string, type: 'info' | 'error' | 'success'): void {
    if (!this.licenseStatusEl) return;
    this.licenseStatusEl.textContent = msg;
    this.licenseStatusEl.style.display = 'block';

    if (type === 'error') {
      this.licenseStatusEl.style.background = '#fef2f2';
      this.licenseStatusEl.style.color = '#dc2626';
      this.licenseStatusEl.style.border = '1px solid #fecaca';
    } else if (type === 'success') {
      this.licenseStatusEl.style.background = '#f0fdf4';
      this.licenseStatusEl.style.color = '#16a34a';
      this.licenseStatusEl.style.border = '1px solid #bbf7d0';
    } else {
      this.licenseStatusEl.style.background = '#f8fafc';
      this.licenseStatusEl.style.color = '#475569';
      this.licenseStatusEl.style.border = '1px solid #e2e8f0';
    }
  }

  private syncLicenseUI(): void {
    const isPro = this.licenseService.isProActivated();
    const stored = this.licenseService.getStoredLicense();

    if (isPro && stored) {
      if (this.licenseInactiveView) this.licenseInactiveView.style.display = 'none';
      if (this.licenseActiveView) this.licenseActiveView.style.display = 'block';
      if (this.activeKeyDetailsEl) {
        // キーの一部をマスクして表示 (例: ABC1-****-9XYZ)
        const raw = stored.key;
        const masked = raw.length > 8 ? `${raw.slice(0, 4)}-****-${raw.slice(-4)}` : '****';
        this.activeKeyDetailsEl.textContent = `License: ${masked}`;
      }
    } else {
      if (this.licenseInactiveView) this.licenseInactiveView.style.display = 'block';
      if (this.licenseActiveView) this.licenseActiveView.style.display = 'none';
      if (this.inputKeyEl) this.inputKeyEl.value = '';
    }
  }

  updateTexts(): void {
    const texts = t().upgrade;
    if (this.featureTitleEl) this.featureTitleEl.textContent = texts.title;
    if (this.headingEl) this.headingEl.textContent = texts.heading;
    if (this.perksEl) {
      this.perksEl.innerHTML = `
        ・${texts.perkUrl}<br>
        ・${texts.perkPreset}<br>
        ・${texts.perkExport}<br>
        ・${texts.perkAudio}<br>
        ・${texts.perkWobble}
      `;
    }
    if (this.buyBtnEl) this.buyBtnEl.textContent = texts.buyBtn;
    if (this.saleBadgeEl) this.saleBadgeEl.textContent = texts.saleBadge;
    if (this.licenseLabelEl) this.licenseLabelEl.textContent = texts.haveKey;
    if (this.inputKeyEl) this.inputKeyEl.placeholder = texts.keyPlaceholder;
    if (this.btnActivateEl && !this.isActivating) this.btnActivateEl.textContent = texts.activateBtn;
    if (this.activeBadgeTextEl) this.activeBadgeTextEl.textContent = texts.activeStatus;
    if (this.btnDeactivateEl) this.btnDeactivateEl.textContent = texts.deactivateBtn;
    if (this.closeFooterBtnEl) this.closeFooterBtnEl.textContent = texts.closeBtn;
  }
}
